/* eslint-disable react/prop-types */
import SubmitContext from 'context/submit/submitContext'
import PageContext from 'context/page/pageContext'
import BottledJuiceProduct from 'models/BottledJuiceProduct'
import React, { useContext, useEffect, useState } from 'react'
import Select from 'react-select'
import Switch from 'react-switch'
import {
  Button,
  ButtonGroup,
  Col,
  FormGroup,
  Input,
  Row,
  Alert,
} from 'reactstrap'
import { useFormUtils } from 'views/shared/useFormUtils'
import {
  flavorOptions,
  juiceTypeOptions,
  bottledContainerSizeOptions,
  concentrateContainerSizeOptions,
} from './constants'
import { Controller, ErrorMessage, useFormContext } from 'react-hook-form'
import { isNotAlpha } from 'context/Utilities'

const JuicePackage = ({ model, packagingInitialState }) => {
  const submitContext = useContext(SubmitContext)
  const pageContext = useContext(PageContext)

  const { current, setCurrent, updating } = submitContext
  const { setScrollToTop } = pageContext

  const [packaging, setPackaging] = useState(packagingInitialState)

  const { setSelect, setSwitch, onChange, asterisk } = useFormUtils()

  const [juiceTypeSelected, setJuiceTypeSelected] = useState(
    juiceTypeOptions[0]
  )

  const methods = useFormContext()

  const setJuiceType = selected => {
    setJuiceTypeSelected(selected)
    setCurrent({ ...current, type: '' })
  }

  const optionsReset = () => {
    methods.reset(
      {
        ContainerType: '',
      },
      {
        errors: false, // errors will not be reset
        dirtyFields: false, // dirtyFields will not be reset
        dirty: false, // dirty will not be reset
        isSubmitted: false,
        touched: false,
        isValid: false,
        submitCount: false,
      }
    )
  }

  useEffect(() => {
    setScrollToTop(true)
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (current?.category === packagingInitialState.category) {
      const {
        category,
        subCategory,
        JuiceFlavor,
        otherFlavor,
        ContainerType,
        VitaminCCertification,
        OneHundredPercentJuiceCertification,
        FrontLabelCertification,
        JuiceType,
        BlendFlavor,
      } = current
      setPackaging({
        category,
        subCategory,
        JuiceFlavor: {
          value: JuiceFlavor ?? '',
          label: JuiceFlavor?.length > 0 ? JuiceFlavor : 'Juice Flavor',
        },
        otherFlavor: otherFlavor,
        ContainerType: {
          value: ContainerType ?? '',
          label: ContainerType?.length > 0 ? ContainerType : 'Container Size',
        },
        VitaminCCertification: VitaminCCertification ?? false,
        OneHundredPercentJuiceCertification:
          OneHundredPercentJuiceCertification ?? false,
        FrontLabelCertification: FrontLabelCertification ?? false,
        JuiceType: JuiceType,
        BlendFlavor: BlendFlavor,
      })
    } else if (!updating) {
      setCurrent(BottledJuiceProduct)
      setJuiceTypeSelected('Bottled')
    } else {
      if (current.JuiceType === 'Bottled') {
        setJuiceTypeSelected('Bottled')
      } else {
        setJuiceTypeSelected('Concentrate')
      }
    }
    // eslint-disable-next-line
  }, [current])

  useEffect(() => {
    if (updating && juiceTypeSelected === 'Concentrate') {
      if (
        current?.ContainerType !== '64 oz shelf-stable only' &&
        current?.ContainerType !== ''
      ) {
        methods.setValue('ContainerType', {
          value: current?.ContainerType,
          label: current?.ContainerType,
        })
      }
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [juiceTypeSelected])

  useEffect(() => {
    if (updating) {
      if (current?.JuiceFlavor === 'Blend') {
        methods.setValue('BlendFlavor', current.BlendFlavor)
      }
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updating])

  return (
    <Row>
      <Col xs={12} md={6} lg={6}>
        <p>
          <strong>Juice Type {asterisk}</strong>
        </p>
        <ButtonGroup>
          <Button
            outline
            color="success"
            className="btn-round"
            onClick={() => {
              optionsReset()
              setJuiceType(juiceTypeOptions[0])
              setCurrent({
                ...current,
                subCategory: '002',
                productType: juiceTypeOptions[0],
                ContainerType: bottledContainerSizeOptions[1].value,
                JuiceType: juiceTypeOptions[0],
              })
            }}
            active={juiceTypeSelected === juiceTypeOptions[0]}
            size="sm"
          >
            {juiceTypeOptions[0]}
          </Button>
        </ButtonGroup>
        <ButtonGroup>
          <Button
            outline
            color="success"
            className="btn-round"
            onClick={() => {
              optionsReset()
              setJuiceType(juiceTypeOptions[1])
              setCurrent({
                ...current,
                subCategory: '',
                productType: juiceTypeOptions[1],
                ContainerType: '',
                JuiceType: juiceTypeOptions[1],
              })
            }}
            active={juiceTypeSelected === juiceTypeOptions[1]}
            size="sm"
          >
            {juiceTypeOptions[1]}
          </Button>
        </ButtonGroup>
        <br />
        <p>
          <strong>Juice Flavor {asterisk}</strong>
        </p>
        <Controller
          as={Select}
          className="react-select primary"
          classNamePrefix="react-select"
          value={packaging.JuiceFlavor}
          options={flavorOptions}
          name="JuiceFlavor"
          control={methods.control}
          rules={{ required: true }}
          placeholder={'Select Juice Flavor'}
          onChange={([selected]) => {
            setSelect(selected, 'JuiceFlavor')
            return selected
          }}
        />
        <p className={'text-danger'}>
          <ErrorMessage
            errors={methods.errors}
            name="JuiceFlavor"
            message={'⚠ This selection is required.'}
            as={<Alert color={'danger'} className={'mt-3'} />}
          />
        </p>
        <br />
        {current?.JuiceFlavor === 'Blend' && (
          <FormGroup>
            <p>
              <strong>Blend Flavor {asterisk}</strong>
            </p>
            <Controller
              as={Input}
              name="BlendFlavor"
              value={packaging.BlendFlavor}
              control={methods.control}
              rules={{
                required: {
                  value: true,
                  message: '⚠ Blend flavor is required.',
                },
                maxLength: {
                  value: 50,
                  message: '⚠ Blend flavor exceeds 50 characters.',
                },
              }}
              onKeyDown={e => {
                if (isNotAlpha(e.key) && e.key !== ' ') {
                  e.preventDefault()
                }
              }}
              onChange={([event]) => {
                onChange(event)
                return event
              }}
            />
            <p className={'text-danger'}>
              <ErrorMessage
                errors={methods.errors}
                name="BlendFlavor"
                as={<Alert color={'danger'} className={'mt-3'} />}
              />
            </p>
          </FormGroup>
        )}
        <p>
          <strong>Size {asterisk}</strong>
        </p>
        {juiceTypeSelected === 'Concentrate' && (
          <Controller
            as={Select}
            className="react-select primary"
            classNamePrefix="react-select"
            options={concentrateContainerSizeOptions}
            name="ContainerType"
            control={methods.control}
            rules={{ required: true }}
            placeholder={'Select Size'}
            defaultValue={current?.containerType}
            onChange={([selected]) => {
              setSelect(selected, 'ContainerType')
              return selected
            }}
          />
        )}
        {juiceTypeSelected === 'Bottled' && (
          <Input defaultValue={bottledContainerSizeOptions[1].value} disabled />
        )}

        <p className={'text-danger'}>
          <ErrorMessage
            errors={methods.errors}
            name="ContainerType"
            message={'⚠ This selection is required.'}
            as={<Alert color={'danger'} className={'mt-3'} />}
          />
        </p>
        <br />
      </Col>
      <Col xs={12} md={6} lg={6}>
        {/*         <p>
          <strong>80% (or more) Vitamin C {asterisk}</strong>
        </p>
        <Controller
          as={Switch}
          name="VitaminCCertification"
          id="VitaminCCertification"
          onChange={([checked, event, id]) => {
            setSwitch(checked, event, id)
            return checked
          }}
          control={methods.control}
          defaultValue={packaging.VitaminCCertification}
          rules={{
            validate: VitaminCCertification => VitaminCCertification === true,
          }}
        />
        <br />
        <p className={'text-danger'}>
          <ErrorMessage
            errors={methods.errors}
            name="VitaminCCertification"
            message={'⚠ Product must be 80% (or more) Vitamin C.'}
            as={<Alert color={'danger'} className={'mt-3'} />}
          />
        </p>
        <hr />
        <p>
          <strong>100% Juice (Unsweetened) {asterisk}</strong>
        </p>
        <Controller
          as={Switch}
          name="OneHundredPercentJuiceCertification"
          id="OneHundredPercentJuiceCertification"
          onChange={([checked, event, id]) => {
            setSwitch(checked, event, id)
            return checked
          }}
          control={methods.control}
          defaultValue={packaging.OneHundredPercentJuiceCertification}
          rules={{
            validate: OneHundredPercentJuiceCertification =>
              OneHundredPercentJuiceCertification === true,
          }}
        />
        <br />
        <p className={'text-danger'}>
          <ErrorMessage
            errors={methods.errors}
            name="OneHundredPercentJuiceCertification"
            message={'⚠ Product must be 100% juice (unsweetened).'}
            as={<Alert color={'danger'} className={'mt-3'} />}
          />
        </p> 
        <hr /> */}
        <p>
          <strong>
            Front label includes statements indicating the product contains 80%
            (or more) Vitamin C and is 100% Juice. Orange juice and white
            grapefruit juice are not required to have Vitamin C statement on the
            front label; however, the submitter must check this box for
            submission purposes. {asterisk}
          </strong>
        </p>
        <Controller
          as={Switch}
          name="FrontLabelCertification"
          id="FrontLabelCertification"
          onChange={([checked, event, id]) => {
            setSwitch(checked, event, id)
            return checked
          }}
          control={methods.control}
          defaultValue={packaging.FrontLabelCertification}
          rules={{
            validate: FrontLabelCertification =>
              FrontLabelCertification === true,
          }}
        />
        <br />
        <p className={'text-danger'}>
          <ErrorMessage
            errors={methods.errors}
            name="FrontLabelCertification"
            message={'⚠ Product must meet labeling requirements.'}
            as={<Alert color={'danger'} className={'mt-3'} />}
          />
        </p>
      </Col>
    </Row>
  )
}

export default JuicePackage

export const getJuicePackage = newProduct => {
  let newJuicePackage = {
    UPC: newProduct.UPC,
    JuiceFlavor: newProduct.JuiceFlavor,
    ContainerType: newProduct.ContainerType,
    VitaminCCertification: newProduct.VitaminCCertification,
    OneHundredPercentJuiceCertification:
      newProduct.OneHundredPercentJuiceCertification,
    FrontLabelCertification: newProduct.FrontLabelCertification,
    JuiceType: newProduct.JuiceType,
  }
  if (newJuicePackage.JuiceFlavor === 'Blend') {
    newJuicePackage.BlendFlavor = newProduct.BlendFlavor
  }
  return newJuicePackage
}

export const updateJuicePackage = newProduct => {
  let newJuicePackage = {
    UPC: newProduct.UPC,
    JuiceType: newProduct.JuiceType,
    ContainerType: newProduct.ContainerType,
    VitaminCCertification: newProduct.VitaminCCertification,
    OneHundredPercentJuiceCertification:
      newProduct.OneHundredPercentJuiceCertification,
    FrontLabelCertification: newProduct.FrontLabelCertification,
    JuiceFlavor: newProduct.JuiceFlavor,
  }
  if (newJuicePackage.JuiceFlavor === 'Blend') {
    newJuicePackage.BlendFlavor = newProduct.BlendFlavor
  }
  return newJuicePackage
}
